import axios from './axios'

export interface CampaignAttachment {
  id: string
  name: string
  size: number
  type: string
  blob_key: string
  url: string
}

export interface CampaignAttachmentUploadResponse {
  id: string
  name: string
  size: number
  type: string
  blob_key: string
  url: string
}

class CampaignAttachmentsApi {
  // Upload file attachment to campaign
  async uploadFile(
    campaignId: string,
    file: File
  ): Promise<CampaignAttachmentUploadResponse> {
    const formData = new FormData()
    formData.append('file', file)

    const response = await axios.post(`/campaigns/${campaignId}/attachments/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    
    return response.data.data
  }

  // Link file manager files to campaign
  async linkFileManagerFiles(
    campaignId: string,
    fileManagerIds: string[]
  ): Promise<CampaignAttachmentUploadResponse[]> {
    
    const response = await axios.post(`/campaigns/${campaignId}/attachments/link_file_manager`, {
      file_manager_ids: fileManagerIds
    })
    
    
    return response.data.data.attached_files
  }

  // Delete file attachment from campaign
  async deleteFile(campaignId: string, attachmentId: string): Promise<void> {
    await axios.delete(`/campaigns/${campaignId}/attachments/${attachmentId}`)
  }

  // Validate file before upload
  validateFile(file: File): { valid: boolean; error?: string } {
    const maxSize = 10 * 1024 * 1024 // 10MB in bytes
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'text/plain',
      'text/csv',
      'image/jpeg',
      'image/jpg', // non-standard MIME some browsers/OSes report for .jpg → was rejected as "Invalid file type"
      'image/png',
      'image/gif',
      'image/webp',
      'application/zip',
      'application/x-zip-compressed'
    ]

    // File extensions as fallback (some PDFs without .pdf extension report as octet-stream)
    const allowedExtensions = [
      '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx',
      '.txt', '.csv', '.jpg', '.jpeg', '.png', '.gif', '.webp', '.zip'
    ]

    if (file.size > maxSize) {
      return {
        valid: false,
        error: 'File size too large. Maximum size for email attachments is 10MB.'
      }
    }

    // Get file extension
    const fileName = file.name.toLowerCase()
    const fileExtension = fileName.substring(fileName.lastIndexOf('.'))
    const hasValidExtension = allowedExtensions.includes(fileExtension)

    // Check MIME type first, fallback to extension for files without proper MIME type
    const isValidMimeType = allowedTypes.includes(file.type)
    const isMimeTypeUnknown = !file.type || file.type === 'application/octet-stream'

    if (!isValidMimeType && !(isMimeTypeUnknown && hasValidExtension)) {
      return {
        valid: false,
        error: 'Invalid file type. Only PDF, Office documents, images, text files and ZIP archives are allowed.'
      }
    }

    return { valid: true }
  }

  // Format file size for display
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
  }
}

export const campaignAttachmentsApi = new CampaignAttachmentsApi()
export default campaignAttachmentsApi

